import { useCallback } from 'react';
import { useMetricConfig } from 'apps/sigma2-ui/src/lib/hooks/useMetricConfig';
import { safeParseFloat } from 'libs/end-to-end/src/math';
import { formatNumberForDisplayInUnits } from 'libs/end-to-end/src/number-formatting';
import { determineUnitOfMetric } from 'libs/end-to-end/src/metrics';
import { NumericDisplayUnit } from 'libs/end-to-end/src/metrics';

interface FormatCellValueResult {
  formatCellValue: (params: {
    rawValue: string;
    header: string;
    units?: NumericDisplayUnit;
  }) => string;
}

export const useFormatCellValue = (): FormatCellValueResult => {
  const { getMetricAndFocusAreaFromColumnName } = useMetricConfig();

  const formatCellValue = useCallback(
    // eslint-disable-next-line complexity
    ({
      rawValue,
      header,
      units,
    }: {
      rawValue: string;
      header: string;
      units?: NumericDisplayUnit;
    }) => {
      const metricInfo = getMetricAndFocusAreaFromColumnName({
        columnName: header,
      });

      const noValue =
        rawValue === undefined || rawValue === null || rawValue === '';
      const valueParsed = rawValue ? safeParseFloat(rawValue) : null;

      if (noValue || valueParsed === null) {
        return 'N/A';
      }

      if (units) {
        return formatNumberForDisplayInUnits({
          units,
          isPercentageAlreadyMultipliedBy100: false,
          value: valueParsed,
        });
      }

      if (!metricInfo) {
        return formatNumberForDisplayInUnits({
          units: 'count',
          isPercentageAlreadyMultipliedBy100: false,
          value: valueParsed,
        });
      }

      const metricUnits = determineUnitOfMetric({
        knownMaximum: null,
        isMetricBoolean: metricInfo.metric.isMetricBoolean,
        metricFocusArea: metricInfo.focusArea,
        definitionUnit: metricInfo.metric.unit,
      });

      return formatNumberForDisplayInUnits({
        units: metricUnits,
        isPercentageAlreadyMultipliedBy100: false,
        value: valueParsed,
      });
    },
    [getMetricAndFocusAreaFromColumnName],
  );

  return { formatCellValue };
};
